import { Children, FC, ReactNode, isValidElement } from 'react'
import { Box } from '@mui/material'
import Markdown from 'markdown-to-jsx'
import { Figure } from './Figure'
import { Callout } from './Callout'
import { headingId } from '../core'
import { colorTokens } from '../../../shared/styles/colorTokens'
import { FONT_WEIGHT, typeScale } from '../../../shared/styles/typography'

type MarkdownBodyProps = {
  children: string
}

const toText = (node: ReactNode): string =>
  Children.toArray(node)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child)
      }
      if (isValidElement<{ children?: ReactNode }>(child)) {
        return toText(child.props.children)
      }
      return ''
    })
    .join('')

/**
 * 手順の見出し。目次から飛べるよう、見出しの文言から起こした id を振る。
 * 目次側 (core.ts の extractSteps) と同じ headingId を使うので、
 * md の見出しを直せば両方が一度に追従する。
 *
 * 記号で始まらない見出しは id を持てない（extractSteps が dev で警告する）。
 * その場合は id="" を吐かず属性ごと落とす。
 */
const StepHeading: FC<{ children?: ReactNode }> = ({ children }) => {
  const id = headingId(toText(children))
  return <h3 id={id || undefined}>{children}</h3>
}

/**
 * ガイド本文（content/*.md）のレンダラ。
 *
 * 文章は md 側、体裁はここが持つ。md に書けるのは見出し・箇条書き・
 * 番号付き・画像（![キャプション](/guide/x.png)）・<Callout> のみ。
 *
 * 見出しは md の ### (手順の見出し) だけで、型は h4 (16/600)。
 * 区分見出し h2 (24) はページ側 (index.tsx) が持つ。
 * 罫線では区切らず、見出しの上の余白で区切る。手順の上 32px は
 * 区分の上 64px の半分で、階層を余白でも示している。
 * h4 を h3 (20) に上げると区分と 4px 差になり、境目が消える。
 */
export const MarkdownBody: FC<MarkdownBodyProps> = ({ children }) => (
  <Box
    sx={{
      color: colorTokens.fg.default,
      // 手順の見出し。区分見出し(24/上64px)の半分の余白で、階層を余白でも示す
      '& h3': {
        ...typeScale.h4,
        mt: 4,
        mb: 1,
        scrollMarginTop: '80px',
      },
      '& p': { ...typeScale.body, my: 1 },
      '& ul, & ol': { ...typeScale.body, my: 1, pl: 3 },
      '& li': { my: 0.5 },
      '& li > ul, & li > ol': { my: 0.5 },
      '& strong': { fontWeight: FONT_WEIGHT.semibold },
      '& a': { color: colorTokens.accent.primary, textDecoration: 'none' },
    }}
  >
    <Markdown
      options={{
        overrides: {
          h3: { component: StepHeading },
          img: { component: Figure },
          Callout: { component: Callout },
        },
      }}
    >
      {children}
    </Markdown>
  </Box>
)
